import { initialEmailSignatureDesignConfig } from '../reducers/email-signature-reducer';
import { initialEmailSignatureContextValues } from '@reducers/email-signature-reducer';
import canvg from 'canvg';
import path from 'path';

// utils
import {
  createOrderedCardFields,
  getSuggestionsFromURLFieldValue,
  getURIFromURLFieldValue,
  getURLSuggestionsByFieldType,
} from '@modules/card-helpers';
import {
  filter,
  forEach,
  isEmpty,
  isEqual,
  keyBy,
  lowerCase,
  map,
  mapValues,
  orderBy,
  pick,
  takeRight,
} from 'lodash';
import { generateEntityId } from '@modules/helpers';
import { drawRoundRect } from '@modules/draw-round-rect';
import { isFirefox } from '@modules/document-helpers';
import { formatUrlType } from './formatters';

// types
import {
  EmailSignatureBannerId,
  EmailSignatureEnvironmentalFooterId,
  EmailSignatureContactDetailsField,
  EmailSignatureLegalDisclaimerId,
  EmailSignatureV2,
  EmailSignatureSocialLink,
  EmailSignatureImageFieldId,
  EmailSignatureImagesTypes,
  EmailSignatureImage,
  EmailSignatureIconName,
  EmailSignatureProxyImage,
} from '../components/EmailSignaturev2/types';
import {
  EmailSignatureContactDetailsFieldId,
  EmailSignatureContactDetailsFields,
  EmailSignatureContactDetailsFieldsDesign,
  EmailSignatureContactDetailsFieldTypes,
  EmailSignatureContactDetailsIndices,
  EmailSignatureDesign,
  EmailSignatureSocialColors,
  EmailSignatureSocialLinkDesign,
  EmailSignatureSocialLinkId,
  EmailSignatureSocialLinks,
  EmailSignatureSocialLinksIndices,
  EmailSignatureSocialLinkTypes,
} from '@components/EmailSignaturev2/types';

import {
  EM_SIG_EM_SIG_CONTACT_ICON_SIZE_ACTUAL_SIZE,
  EM_SIG_SOCIAL_ICON_SQUIRCLE_RADIUS,
  EM_SIG_SOCIAL_ICON_SIZES_DICT,
  EM_SIG_IMAGE_SQUIRCLE_RADIUS,
  EM_SIG_FONT_SIZE_BASE,
  EM_SIG_MAXIMUM_HALF_WIDTH_CONTACT_FIELD_LENGTH,
  BRAND_COLORS,
  EmailSignatureContactDetailsFieldWidth,
  EmailSignatureFieldPrefixes,
  EMAIL_SIGNATURE_ENVIRONMENTAL_FOOTER_ICON_SIZE,
  EM_SIG_CONTACT_ICON_SIZE,
} from '../components/EmailSignaturev2/consts';
import {
  EmailSignatureFillStyle,
  EmailSignatureSizes,
  EmailSignaturePersonalDetailsFields,
  EmailSignatureShapes,
} from '../components/EmailSignaturev2/types';
import { Card, CardUrl, CardUrlType, MultiValuedComplexFields, User } from 'types';
import ReactDOMServer from 'react-dom/server';
import EmailSignatureIcon from '@components/EmailSignaturev2/EmailSignatureIcon';
import {
  NEXT_PUBLIC_BLINQ_FORWARD_BASE_URL,
  NEXT_PUBLIC_EMAIL_SIGNATURE_URL,
  NEXT_PUBLIC_FIREBASE_PROJECT_ID,
} from '@env';
import { apolloClient, gql } from '@services/apollo';
import { logger } from './logger';
import { buildUrl } from './url-helpers';

// These declaration is for the typescript bug in Clipboard API where the ClipboardItemData can be Blob
// but typescript spurn the build because the it only accepts Promise<Blob | string>
interface ClipboardItem {
  readonly types: string[];
  readonly presentationStyle: 'unspecified' | 'inline' | 'attachment';
  getType(): Promise<Blob>;
}

interface ClipboardItemData {
  [mimeType: string]: Blob | string | Promise<Blob | string>;
}

declare const ClipboardItem: {
  prototype: ClipboardItem;
  new (itemData: ClipboardItemData): ClipboardItem;
};

/**
 * Convert the contact icon svg to image
 * @param iconMarkup The svg markup of the icon to convert
 * @param backgroundColor The background color of the icon image
 * @returns The local data url of the image
 */
const convertContactIconSVGToImage = (iconMarkup: string, backgroundColor: string): string | undefined => {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) return;

  const size = EM_SIG_EM_SIG_CONTACT_ICON_SIZE_ACTUAL_SIZE;

  canvas.width = size * 2;
  canvas.height = size * 2;

  ctx.scale(2, 2);

  ctx.beginPath();
  ctx.arc(size / 2, size / 2, size / 2, 0, 2 * Math.PI, false);
  ctx.fillStyle = backgroundColor;
  ctx.fill();

  const canvgRenderer = canvg.fromString(ctx, iconMarkup, {
    ignoreDimensions: true,
    ignoreClear: true,
    offsetX: size / 4,
    offsetY: size / 4,
    scaleWidth: size / 2,
    scaleHeight: size / 2,
  });
  canvgRenderer.start();

  const img = canvas.toDataURL('image/png');
  return img;
};

/**
 * Convert the social icon svg to image
 * @param iconMarkup The svg markup of the icon to convert
 * @param backgroundColor The background color of the icon image
 * @param shape The shape of the icon image, only supports Circle, Square and Squircle
 * @param fillStyle The fill style of the icon image, only supports Full and Light
 * @param chosenSize The size of the icon image, only supports Small, Medium and Large
 * @returns The local data url of the image
 */
const convertSocialIconSVGToImage = (
  iconMarkup: string,
  backgroundColor: string,
  shape: EmailSignatureShapes,
  fillStyle: EmailSignatureFillStyle,
  chosenSize: EmailSignatureSizes,
  addPadding = true
): string | undefined => {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) return;

  const strokePadding = fillStyle === EmailSignatureFillStyle.LIGHT ? 4 : 0; // Need padding for stroke
  const contentSize = EM_SIG_SOCIAL_ICON_SIZES_DICT[chosenSize];
  const size = contentSize + strokePadding;

  canvas.width = size * 2;
  canvas.height = size * 2;

  ctx.scale(2, 2);

  const fillColor = fillStyle === EmailSignatureFillStyle.FULL ? backgroundColor : 'white';

  if (shape === EmailSignatureShapes.CIRCLE) {
    ctx.beginPath();
    ctx.lineWidth = 2;
    ctx.arc(size / 2, size / 2, size / 2 - 1, 0, 2 * Math.PI, false);
    ctx.fillStyle = fillColor;
    ctx.fill();

    // Makes sure the logos are cropped within the circle.
    ctx.arc(size / 2, size / 2, size / 2 - 1, 0, 2 * Math.PI, false);
    ctx.clip();
  } else if (shape === EmailSignatureShapes.SQUARE || shape === EmailSignatureShapes.SQUIRCLE) {
    const calculatedBorderRadius = Math.min(
      (EM_SIG_SOCIAL_ICON_SQUIRCLE_RADIUS / 100) * contentSize,
      (EM_SIG_SOCIAL_ICON_SQUIRCLE_RADIUS / 100) * contentSize
    );
    const borderRadius = shape === EmailSignatureShapes.SQUARE ? 0 : calculatedBorderRadius;

    drawRoundRect(ctx, 1, 1, size - 2, size - 2, borderRadius, fillColor, backgroundColor, false);
  }

  if (fillStyle === EmailSignatureFillStyle.LIGHT) {
    ctx.strokeStyle = backgroundColor;
    ctx.stroke();
  }

  if (addPadding) {
    const canvgRenderer = canvg.fromString(ctx, iconMarkup, {
      ignoreDimensions: true,
      ignoreClear: true,
      offsetX: contentSize / 4 + strokePadding / 2,
      offsetY: contentSize / 4 + strokePadding / 2,
      scaleWidth: contentSize / 2,
      scaleHeight: contentSize / 2,
    });

    canvgRenderer.start();
  } else {
    const canvgRenderer = canvg.fromString(ctx, iconMarkup, {
      ignoreDimensions: true,
      ignoreClear: true,
      scaleHeight: size,
      offsetY: 4,
      offsetX: 4,
    });

    canvgRenderer.start();
  }

  const img = canvas.toDataURL('image/png');
  return img;
};

/**
 * Convert the environmental footer svg to image
 * @param iconMarkup The svg markup of the icon to convert
 * @returns The local data url of the image
 */
const convertEnvironmentalFooterIconSVGToImage = (iconMarkup: string): string | undefined => {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) return;

  canvas.width = EMAIL_SIGNATURE_ENVIRONMENTAL_FOOTER_ICON_SIZE * window.devicePixelRatio;
  canvas.height = EMAIL_SIGNATURE_ENVIRONMENTAL_FOOTER_ICON_SIZE * window.devicePixelRatio;
  ctx.scale(window.devicePixelRatio, window.devicePixelRatio);

  const canvgRenderer = canvg.fromString(ctx, iconMarkup, {
    ignoreDimensions: true,
    ignoreClear: true,
    offsetX: EMAIL_SIGNATURE_ENVIRONMENTAL_FOOTER_ICON_SIZE / 4,
    offsetY: EMAIL_SIGNATURE_ENVIRONMENTAL_FOOTER_ICON_SIZE / 4 - 1, // a bit of padding to ensure the icon is in the center
    scaleWidth: EMAIL_SIGNATURE_ENVIRONMENTAL_FOOTER_ICON_SIZE / 2,
    scaleHeight: EMAIL_SIGNATURE_ENVIRONMENTAL_FOOTER_ICON_SIZE / 2,
  });

  canvgRenderer.start();

  const img = canvas.toDataURL('image/png');
  return img;
};

/**
 * Crop the image to user's chosen shape. The function only supports 3 shapes, Circle, Square and Squircle
 * @param shape The shape we want to crop
 * @param imageElement The image element that displays the image we want to crop
 * @returns The local data url of the image
 */
const cropEmailSignatureImageToGivenShape = (
  shape: EmailSignatureShapes | EmailSignatureShapes | undefined,
  imageElement: HTMLImageElement
): string | undefined => {
  if (!shape) return;
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) return;

  if (shape === EmailSignatureShapes.CIRCLE) {
    const canvasDimensions = Math.min(imageElement.width, imageElement.height);
    canvas.width = canvasDimensions;
    canvas.height = canvasDimensions;
    const sx = imageElement.width > imageElement.height ? (imageElement.width - imageElement.height) / 2 : 0;
    const sy = imageElement.height > imageElement.width ? (imageElement.height - imageElement.width) / 2 : 0;
    ctx.drawImage(
      imageElement,
      sx,
      sy,
      canvasDimensions,
      canvasDimensions,
      0,
      0,
      canvasDimensions,
      canvasDimensions
    );
    ctx.globalCompositeOperation = 'destination-in';
    ctx.beginPath();
    // I dont know why i need imageReference.width / 2 - 5 but i need it
    ctx.arc(canvasDimensions / 2, canvasDimensions / 2, canvasDimensions / 2 - 5, 0, 2 * Math.PI, false);
    ctx.fill();
  } else if (shape === EmailSignatureShapes.SQUARE || shape === EmailSignatureShapes.SQUIRCLE) {
    canvas.width = imageElement.width;
    canvas.height = imageElement.height;
    const calculatedBorderRadius = Math.min(
      (EM_SIG_IMAGE_SQUIRCLE_RADIUS / 100) * imageElement.width,
      (EM_SIG_IMAGE_SQUIRCLE_RADIUS / 100) * imageElement.height
    );

    const borderRadius = shape === EmailSignatureShapes.SQUARE ? 0 : calculatedBorderRadius;
    drawRoundRect(
      ctx,
      0,
      0,
      imageElement.width,
      imageElement.height,
      borderRadius,
      'rgba(255, 255, 255, 0)',
      'rgba(255, 255, 255, 0)',
      false
    );
    ctx.clip();
    ctx.drawImage(imageElement, 0, 0);
    ctx.restore();
  }

  const img = canvas.toDataURL('image/png');
  return img;
};

export const generateSocialLinkIconImage = (
  socialLinkType: EmailSignatureSocialLinkTypes,
  design: EmailSignatureDesign
): string | undefined => {
  const width = EM_SIG_SOCIAL_ICON_SIZES_DICT[design.socialLinksSize] / 2;
  const height = EM_SIG_SOCIAL_ICON_SIZES_DICT[design.socialLinksSize] / 2;

  const iconMarkup = ReactDOMServer.renderToStaticMarkup(
    <EmailSignatureIcon
      icon={socialLinkType}
      inlineStyle={{
        width: `${width}px`,
        height: `${height}px`,
        color:
          design.socialLinksFillStyle === EmailSignatureFillStyle.FULL
            ? 'white'
            : getSocialLinkIconColor(socialLinkType, design),
      }}
    />
  );

  const iconImageDataUrl = convertSocialIconSVGToImage(
    iconMarkup,
    getSocialLinkIconColor(socialLinkType, design),
    design.socialLinksShape,
    design.socialLinksFillStyle,
    design.socialLinksSize,
    true
  );

  return iconImageDataUrl;
};

export const generateContactDetailsIconImage = (
  contactDetailsFieldBaseIcon: EmailSignatureIconName,
  contactFieldsIconsColor: string
): string | undefined => {
  const iconMarkup = ReactDOMServer.renderToStaticMarkup(
    <EmailSignatureIcon
      icon={contactDetailsFieldBaseIcon}
      inlineStyle={{
        width: `${EM_SIG_CONTACT_ICON_SIZE}px`,
        height: `${EM_SIG_CONTACT_ICON_SIZE}px`,
        color: 'white',
      }}
    />
  );
  const iconImageDataUrl = convertContactIconSVGToImage(iconMarkup, contactFieldsIconsColor);
  return iconImageDataUrl;
};
export const generateNewEmailSignatureFieldId = (): string => {
  return generateEntityId(26);
};

export const generateNewEmailSignatureContactFieldId = (): EmailSignatureContactDetailsFieldId => {
  return `${EmailSignatureFieldPrefixes.CONTACT}${generateNewEmailSignatureFieldId()}`;
};

export const generateNewEmailSignatureSocialLinkId = (): EmailSignatureSocialLinkId => {
  return `${EmailSignatureFieldPrefixes.SOCIAL}${generateNewEmailSignatureFieldId()}`;
};

export const generateNewEmailSignatureCustomBannerId = (): EmailSignatureBannerId => {
  return `${EmailSignatureFieldPrefixes.BANNER}${generateNewEmailSignatureFieldId()}`;
};

export const generateNewEnvironmentalFooterId = (): EmailSignatureEnvironmentalFooterId => {
  return `${EmailSignatureFieldPrefixes.ENVIRONMENTAL_FOOTER}${generateNewEmailSignatureFieldId()}`;
};

export const generateNewLegalDisclaimerId = (): EmailSignatureLegalDisclaimerId => {
  return `${EmailSignatureFieldPrefixes.LEGAL_DISCLAIMER}${generateNewEmailSignatureFieldId()}`;
};

export const generateNewImageId = (): EmailSignatureImageFieldId => {
  return `${EmailSignatureFieldPrefixes.IMAGE}${generateNewEmailSignatureFieldId()}`;
};

/**
 * Check if the object is actually empty, the difference between this and isEmpty from lodash is
 * that this will also check the values of the keys not the just if the object has any keys
 * For example, for an object like { a: "", b: "" }, the function will return true
 * @param objectToCheck
 */
export const isObjectActuallyEmpty = (objectToCheck: Record<string | number | symbol, unknown>): boolean => {
  const values = Object.values(objectToCheck);

  return !values.some((element) => {
    if (typeof element !== 'number') return !isEmpty(element);
    return true;
  });
};

/**
 * Format a phone number so it can be displayed in the email signature
 * @param phoneNumber The phone number to display
 * @param extension The extension numbers to dial
 */
export function formatPhoneNumber(phoneNumber: string, extension: string | undefined): string {
  if (!extension) return phoneNumber;
  else return `${phoneNumber}${formatEmailSignaturePhoneExtension(extension)}`;
}

export const formatEmailSignaturePhoneExtension = (extension: string): string => {
  return ` Ext. ${extension}`;
};

/**
 * Measure the text width of a given text based on the font and EM_SIG_FONT_SIZE_BASE
 * @param textToMeasure
 * @param font The font of the textToMeasure
 */
export const measureTextWidth = (textToMeasure: string, font: string): number => {
  const canvasToMeasureTextWidth = document.createElement('canvas');
  const context = canvasToMeasureTextWidth.getContext('2d');
  if (!context) return 0;
  context.font = `${EM_SIG_FONT_SIZE_BASE}px ${font}`;
  return context.measureText(textToMeasure).width;
};

/**
 * Check if a contact field has exceeded the half-width limit based on the font and EM_SIG_FONT_SIZE_BASE
 * @param contactField The contact field value to check
 * @param font The font of the contactField
 */
export const contactDetailsFieldExceedsLimit = (contactField: string, font: string): boolean =>
  measureTextWidth(contactField, font) > EM_SIG_MAXIMUM_HALF_WIDTH_CONTACT_FIELD_LENGTH;

export const extractEmailSignaturePersonalDetailsFromCard = (
  card: Card
): EmailSignaturePersonalDetailsFields => {
  return {
    firstName: card.firstName || '',
    lastName: card.lastName || '',
    jobTitle: card.jobTitle ?? '',
    company: card.orgName ?? '',
    headline: card.headline ?? '',
  };
};

/**
 * If the users give us an website link and do not give us the title, we will pick a default
 * title using this function
 * @param url The url of the website link
 */
export const getDefaultTitleForWebsiteIfNoUserInput = (url: string): string => {
  const suggestions = getSuggestionsFromURLFieldValue('link', url);

  return suggestions[0] ?? '';
};

/**
 * Extract the contact details from a given card, the function will mainly extract email, phone
 * address, company website, whatsapp, signal and skype
 * @param card The card we are going to extract contact details from
 * @param emailSignatureDesign The design options values that user have chosen
 * @returns The object key-value that contains all the contact details and contact details orders
 */
export const extractEmailSignatureContactDetailsFieldsFromCard = (
  card: Card,
  emailSignatureDesign: EmailSignatureDesign
): [EmailSignatureContactDetailsFields, EmailSignatureContactDetailsIndices] => {
  const maximumContactFieldWidth = EM_SIG_MAXIMUM_HALF_WIDTH_CONTACT_FIELD_LENGTH;
  const contactDetailsFields: EmailSignatureContactDetailsFields = {};

  const orderedFields = createOrderedCardFields(card);

  function deriveFieldWidth(contactFieldWidth: number, maximumContactFieldWidth: number) {
    if (contactFieldWidth > maximumContactFieldWidth) {
      return EmailSignatureContactDetailsFieldWidth.FULL_WIDTH;
    } else {
      return EmailSignatureContactDetailsFieldWidth.HALF_WIDTH;
    }
  }

  orderedFields.forEach((field) => {
    const newContactFieldId = generateNewEmailSignatureContactFieldId();

    const contactDetailCommonProperties: Pick<EmailSignatureContactDetailsField, 'id' | 'value' | 'iconUrl'> =
      {
        id: newContactFieldId,
        value: field.value.value,
        iconUrl: '',
      };

    switch (field.type) {
      case 'email': {
        const contactFieldWidth = measureTextWidth(field.value.value, emailSignatureDesign.font);

        contactDetailsFields[newContactFieldId] = {
          ...contactDetailCommonProperties,
          type: EmailSignatureContactDetailsFieldTypes.EMAIL,
          value: field.value.value,
          label: field.value.label,
          baseIcon: 'envelope',
          fieldWidth: deriveFieldWidth(contactFieldWidth, maximumContactFieldWidth),
        };

        break;
      }
      case 'address': {
        const contactFieldWidth = measureTextWidth(field.value.value, emailSignatureDesign.font);

        contactDetailsFields[newContactFieldId] = {
          ...contactDetailCommonProperties,
          type: EmailSignatureContactDetailsFieldTypes.ADDRESS,
          value: field.value.value,
          label: field.value.label,
          baseIcon: 'map-marker-alt',
          fieldWidth: deriveFieldWidth(contactFieldWidth, maximumContactFieldWidth),
        };

        break;
      }
      case 'url': {
        if (
          field.value.type !== 'org' &&
          field.value.type !== 'link' &&
          field.value.type !== 'signal' &&
          field.value.type !== 'whatsapp' &&
          field.value.type !== 'skype' &&
          field.value.type !== 'company_url'
        ) {
          return;
        }

        if (field.value.type === 'org' || field.value.type === 'link' || field.value.type === 'company_url') {
          const contactFieldWidth = measureTextWidth(field.value.title, emailSignatureDesign.font);

          contactDetailsFields[newContactFieldId] = {
            ...contactDetailCommonProperties,
            type: EmailSignatureContactDetailsFieldTypes.WEBSITE,
            value: field.value.value,
            label: '',
            title: field.value.title,
            baseIcon: 'browser',
            fieldWidth: deriveFieldWidth(contactFieldWidth, maximumContactFieldWidth),
          };
        } else {
          const contactFieldWidth = measureTextWidth(field.value.userValue, emailSignatureDesign.font);

          contactDetailsFields[newContactFieldId] = {
            ...contactDetailCommonProperties,
            type: field.value.type as EmailSignatureContactDetailsFieldTypes,
            value: field.value.userValue,
            label: '',
            baseIcon: field.value.type,
            fieldWidth: deriveFieldWidth(contactFieldWidth, maximumContactFieldWidth),
          };
        }

        break;
      }
      case 'phoneNumber': {
        const formattedExtension = field.value.extension
          ? formatEmailSignaturePhoneExtension(field.value.extension)
          : '';
        const contactDetailPhoneNumberToDisplay = `${field.value.value}${formattedExtension}`;

        const contactFieldWidth = measureTextWidth(
          contactDetailPhoneNumberToDisplay,
          emailSignatureDesign.font
        );

        contactDetailsFields[newContactFieldId] = {
          ...contactDetailCommonProperties,
          type: EmailSignatureContactDetailsFieldTypes.PHONE,
          value: field.value.value,
          label: field.value.label,
          extension: field.value.extension,
          countryCode: field.value.countryCode || '',
          baseIcon: 'phone-alt',
          fieldWidth: deriveFieldWidth(contactFieldWidth, maximumContactFieldWidth),
        };
      }
    }
  });

  const contactDetailsFieldOrder = Object.keys(contactDetailsFields);

  return [contactDetailsFields, contactDetailsFieldOrder as EmailSignatureContactDetailsIndices];
};

/**
 * Convert the contact details fields of email signatures to appropriate card fields
 * @param emailSignature The email signature we are going to convert
 * @param fieldsOrder An array to represent the fields order in card
 * @param cardComplexFields An object that contains card fields
 */
export const convertEmailSignatureContactDetailsToCardFields = (
  emailSignature: EmailSignatureV2,
  fieldsOrder: string[],
  cardComplexFields: MultiValuedComplexFields
): void => {
  const contactDetails = emailSignature.contactDetails;
  forEach(contactDetails, (emailSignatureContactDetailsField) => {
    if (emailSignatureContactDetailsField.type === EmailSignatureContactDetailsFieldTypes.ADDRESS) {
      if (cardComplexFields.addresses === undefined) {
        cardComplexFields.addresses = [];
      }
      const newAddressId = generateEntityId();
      fieldsOrder.push(newAddressId);
      cardComplexFields.addresses.push({
        id: newAddressId,
        value: emailSignatureContactDetailsField.value,
        label: emailSignatureContactDetailsField.label,
        type: 'org',
        isPreset: false,
      });
    } else if (emailSignatureContactDetailsField.type === EmailSignatureContactDetailsFieldTypes.EMAIL) {
      if (cardComplexFields.emails === undefined) {
        cardComplexFields.emails = [];
      }
      const newEmailId = generateEntityId();
      fieldsOrder.push(newEmailId);
      cardComplexFields.emails.push({
        id: newEmailId,
        value: emailSignatureContactDetailsField.value,
        label: emailSignatureContactDetailsField.label,
        isPreset: false,
      });
    } else if (
      emailSignatureContactDetailsField.type === EmailSignatureContactDetailsFieldTypes.FAX ||
      emailSignatureContactDetailsField.type === EmailSignatureContactDetailsFieldTypes.PHONE
    ) {
      if (cardComplexFields.phoneNumbers === undefined) {
        cardComplexFields.phoneNumbers = [];
      }
      const newFieldId = generateEntityId();
      fieldsOrder.push(newFieldId);
      cardComplexFields.phoneNumbers.push({
        id: newFieldId,
        value: emailSignatureContactDetailsField.value,
        label: emailSignatureContactDetailsField.label,
        extension: emailSignatureContactDetailsField.extension,
        countryCode: emailSignatureContactDetailsField.countryCode,
        isPreset: false,
      });
    } else if (
      emailSignatureContactDetailsField.type === EmailSignatureContactDetailsFieldTypes.WEBSITE ||
      emailSignatureContactDetailsField.type === EmailSignatureContactDetailsFieldTypes.SKYPE ||
      emailSignatureContactDetailsField.type === EmailSignatureContactDetailsFieldTypes.WHATSAPP
    ) {
      if (cardComplexFields.urls === undefined) {
        cardComplexFields.urls = [];
      }
      const cardUrlType =
        emailSignatureContactDetailsField.type === EmailSignatureContactDetailsFieldTypes.WEBSITE
          ? 'org'
          : emailSignatureContactDetailsField.type;
      const newWebsiteId = generateEntityId();
      fieldsOrder.push(newWebsiteId);
      cardComplexFields.urls.push({
        title:
          emailSignatureContactDetailsField.title ||
          getDefaultTitleForWebsiteIfNoUserInput(emailSignatureContactDetailsField.value),
        type: cardUrlType,
        value: addPrefixToContactFieldValue(
          emailSignatureContactDetailsField.value,
          emailSignatureContactDetailsField.type
        ),
        userValue: emailSignatureContactDetailsField.value,
        isPreset: false,
        id: newWebsiteId,
      });
    }
  });
};

/**
 * Extract the social links from a given card
 * @param card The card we are going to extract contact details from
 * @returns The object key-value that contains all the social links and social links orders
 */
export const extractEmailSignatureSocialLinksFromCard = (
  card: Card
): [EmailSignatureSocialLinks, EmailSignatureSocialLinksIndices] => {
  const orderedFields = createOrderedCardFields(card);
  const orderedUrls = orderedFields.filter(({ type }) => type === 'url');

  const socialLinks: EmailSignatureSocialLinks = {};

  orderedUrls.forEach(({ value }) => {
    const cardUrl = value as CardUrl;
    let socialLinkType: EmailSignatureSocialLinkTypes | undefined;
    switch (cardUrl.type) {
      case 'paypal':
        socialLinkType = EmailSignatureSocialLinkTypes.PAYPAL;
        break;
      case 'snapchat':
        socialLinkType = EmailSignatureSocialLinkTypes.SNAPCHAT;
        break;
      case 'tiktok':
        socialLinkType = EmailSignatureSocialLinkTypes.TIKTOK;
        break;
      case 'twitch':
        socialLinkType = EmailSignatureSocialLinkTypes.TWITCH;
        break;
      case 'twitter':
        socialLinkType = EmailSignatureSocialLinkTypes.TWITTER;
        break;
      case 'venmo':
        socialLinkType = EmailSignatureSocialLinkTypes.VENMO;
        break;
      case 'yelp':
        socialLinkType = EmailSignatureSocialLinkTypes.YELP;
        break;
      case 'youtube':
        socialLinkType = EmailSignatureSocialLinkTypes.YOUTUBE;
        break;
      case 'calendly':
        socialLinkType = EmailSignatureSocialLinkTypes.CALENDLY;
        break;
      case 'cashapp':
        socialLinkType = EmailSignatureSocialLinkTypes.CASHAPP;
        break;
      case 'discord':
        socialLinkType = EmailSignatureSocialLinkTypes.DISCORD;
        break;
      case 'facebook':
        socialLinkType = EmailSignatureSocialLinkTypes.FACEBOOK;
        break;
      case 'github':
        socialLinkType = EmailSignatureSocialLinkTypes.GITHUB;
        break;
      case 'instagram':
        socialLinkType = EmailSignatureSocialLinkTypes.INSTAGRAM;
        break;
      case 'linkedin':
        socialLinkType = EmailSignatureSocialLinkTypes.LINKEDIN;
        break;
      default:
        break;
    }

    const newSocialLinkId = generateNewEmailSignatureSocialLinkId();
    if (!socialLinkType) return;

    socialLinks[newSocialLinkId] = {
      id: newSocialLinkId,
      type: socialLinkType,
      iconUrl: '',
      value: cardUrl.value,
    };
  });

  const socialLinksOrder = Object.keys(socialLinks);

  return [socialLinks, socialLinksOrder as EmailSignatureSocialLinksIndices];
};

/**
 * Convert the social links of email signatures to appropriate card fields
 * @param emailSignature The email signature we are going to convert
 * @param fieldsOrder An array to represent the fields order in card
 * @param cardComplexFields An object that contains card fields
 */
export const convertEmailSignatureSocialLinksToCardFields = (
  emailSignature: EmailSignatureV2,
  fieldsOrder: string[],
  cardComplexFields: MultiValuedComplexFields
): void => {
  const socialLinks = emailSignature.socialLinks;
  forEach(socialLinks, (socialLink) => {
    if (cardComplexFields.urls === undefined) {
      cardComplexFields.urls = [];
    }
    const newWebsiteId = generateEntityId();
    fieldsOrder.push(newWebsiteId);
    cardComplexFields.urls.push({
      title:
        getSuggestionsFromURLFieldValue(socialLink.type, socialLink.value)[0] ??
        getURLSuggestionsByFieldType(socialLink.type)[0],
      type: socialLink.type,
      value: socialLink.value,
      userValue: socialLink.value,
      isPreset: false,
      id: newWebsiteId,
    });
  });
};

/**
 * Copy the email signature html to clipboard, when the user pastes the email signature,
 * they will paste what they see instead of html characters
 * @param emailSignatureNode The html node of the actual email signature
 * @param emailSignatureContainerNode The html node of the wrapper div, it is only used if the browser is firefox
 * @param trackingPixel The tracking pixel img src that will be appended to the email signature
 * @returns A boolean that indicates the success status of the operation
 */
function copyEmailSignatureToClipboard(
  emailSignatureNode: HTMLTableElement,
  emailSignatureContainerNode?: HTMLDivElement,
  trackingPixel?: string
): boolean {
  const copy = emailSignatureNode.cloneNode(true) as HTMLTableElement;

  if (trackingPixel) {
    const px = document.createElement('img');
    copy.appendChild(px);
    px.src = trackingPixel;
    px.alt = '';
    px.height = 1;
    px.width = 1;
  } else {
    logger.warn('No tracking pixel found to append');
  }

  try {
    if (!isFirefox() && navigator.clipboard) {
      const htmlContent = copy.outerHTML;
      const clipboardItemInput = new ClipboardItem({
        'text/html': new Blob([htmlContent], { type: 'text/html' }),
      });
      navigator.clipboard.write([clipboardItemInput]);
      return true;
    } else {
      if (!emailSignatureContainerNode) return false;
      emailSignatureContainerNode.style.display = 'initial';
      const range = document.createRange();
      // select the contents of the emailSignatureNode which is sitting in the DOM
      range.selectNodeContents(emailSignatureNode);
      const selection = window.getSelection();

      if (selection) {
        selection.removeAllRanges();
        selection.addRange(range);
        document.execCommand('copy');
        selection.removeAllRanges();
        emailSignatureContainerNode.style.display = 'none';

        return true;
      }

      return false;
    }
  } catch (e) {
    console.log(e);
    if (emailSignatureContainerNode) emailSignatureContainerNode.style.display = 'none';
    return false;
  }
}

/**
 * Add prefix to the contact details field value based on their type
 * @param value The contact details field value
 * @param type The type of the contact details field
 * @returns The contact details field value with prefix
 */
export function addPrefixToContactFieldValue(
  value: string,
  type: EmailSignatureContactDetailsFieldTypes
): string {
  // if the users give us the value that already has these prefixes(very very unlikely but just to be safe) we need
  // to remove them first and then add the new one.
  const valueWithRemovedPrefix = removePrefixFromContactFieldValue(value, type);
  switch (type) {
    case 'phone':
      return `tel:${valueWithRemovedPrefix.replaceAll(' ', '')}`; // remove spaces
    case 'email':
      return `mailto:${valueWithRemovedPrefix}`;
    case 'address':
      return `https://www.google.com/maps?q=${valueWithRemovedPrefix}`;
    case 'fax':
      return `fax:${valueWithRemovedPrefix}`;
    case 'website':
      return getURIFromURLFieldValue('link', value ?? '') ?? value;
    default:
      return getURIFromURLFieldValue(type as CardUrlType, value ?? '') ?? value;
  }
}

/**
 * Remove prefix from the contact details field value based on their type
 * @param valueWithPrefix The contact details field value with the prefix
 * @param type The type of the contact details field
 * @returns The contact details field value without prefix
 */
export function removePrefixFromContactFieldValue(
  valueWithPrefix: string,
  type: EmailSignatureContactDetailsFieldTypes
): string {
  let result = valueWithPrefix;
  switch (type) {
    // we only do this for phone, email, address, fax because the other types are handled nicely by the card helper
    case 'phone': {
      const separatedStrings = valueWithPrefix.split('tel:');
      if (separatedStrings.length === 2) result = separatedStrings[1];
      break;
    }
    case 'email': {
      const separatedStrings = valueWithPrefix.split('mailto:');
      if (separatedStrings.length === 2) result = separatedStrings[1];

      break;
    }
    case 'address': {
      const separatedStrings = valueWithPrefix.split('https://www.google.com/maps?q=');
      if (separatedStrings.length === 2) result = separatedStrings[1];
      break;
    }
    case 'fax': {
      const separatedStrings = valueWithPrefix.split('fax:');
      if (separatedStrings.length === 2) result = separatedStrings[1];
      break;
    }
    default:
      break;
  }
  return result;
}

/**
 * Get all the design options values for social links
 * @param emailSignatureDesign
 * @returns The object key-value that contains all design options values for social links
 */
export function getSocialLinksDesign(
  emailSignatureDesign: EmailSignatureDesign
): EmailSignatureSocialLinkDesign {
  return pick(emailSignatureDesign, [
    'socialLinksFillStyle',
    'socialLinksShape',
    'socialLinksSize',
    'socialLinksColorMode',
    'socialLinksColor',
  ]);
}

/**
 * Get the color of the social links based on the design options and social link type
 * @param socialLinkIconType The social link type
 * @param emailSignatureDesign
 */
export function getSocialLinkIconColor(
  socialLinkIconType: EmailSignatureSocialLinkTypes,
  emailSignatureDesign: EmailSignatureDesign
): string {
  if (emailSignatureDesign.socialLinksColorMode === EmailSignatureSocialColors.BRAND) {
    return BRAND_COLORS[socialLinkIconType];
  } else if (emailSignatureDesign.socialLinksColorMode === EmailSignatureSocialColors.THEME) {
    return emailSignatureDesign.themeColor || '';
  } else if (emailSignatureDesign.socialLinksColorMode === EmailSignatureSocialColors.CUSTOM) {
    return emailSignatureDesign.socialLinksColor || '';
  }
  return emailSignatureDesign.themeColor || '';
}

/**
 * Check if the color of social links have changed because the theme color changed
 * @param previousEmailSignatureDesign The previous design options values
 * @param currentEmailSignatureDesign The current design options values
 */
export function socialLinksColorChangedBecauseOfTheme(
  previousEmailSignatureDesign: EmailSignatureDesign,
  currentEmailSignatureDesign: EmailSignatureDesign
): boolean {
  if (
    previousEmailSignatureDesign.socialLinksColorMode === currentEmailSignatureDesign.socialLinksColorMode &&
    previousEmailSignatureDesign.socialLinksColorMode === EmailSignatureSocialColors.THEME
  )
    return !isEqual(
      previousEmailSignatureDesign.themeColor.toUpperCase(),
      currentEmailSignatureDesign.themeColor.toUpperCase()
    );
  else return false;
}

/**
 * Check if the appearance of social links have changed because either the theme color changed
 * or design options values ofr social links changed
 * @param previousEmailSignatureDesign The previous design options values
 * @param currentEmailSignatureDesign The current design options values
 */
export function socialLinksAppearanceHaveChanged(
  previousEmailSignatureDesign: EmailSignatureDesign,
  currentEmailSignatureDesign: EmailSignatureDesign
): boolean {
  const socialLinksDesignHasChanged = !isEqual(
    getSocialLinksDesign(previousEmailSignatureDesign),
    getSocialLinksDesign(currentEmailSignatureDesign)
  );
  return (
    socialLinksDesignHasChanged ||
    socialLinksColorChangedBecauseOfTheme(previousEmailSignatureDesign, currentEmailSignatureDesign)
  );
}

/**
 * Get all the design options values for contact details fields
 * @param emailSignatureDesign
 * @returns The object key-value that contains all design options values for contact details fields
 */
export function getContactDetailsFieldsDesign(
  emailSignatureDesign: EmailSignatureDesign
): EmailSignatureContactDetailsFieldsDesign {
  return pick(emailSignatureDesign, ['contactDetailsIconUseThemeColor', 'contactDetailsIconColor']);
}

/**
 * Check if the color of the contact details fields have changed
 * @param previousEmailSignatureDesign The previous design options values
 * @param currentEmailSignatureDesign The current design options values
 */
export function contactIconsColorHaveChanged(
  previousEmailSignatureDesign: EmailSignatureDesign,
  currentEmailSignatureDesign: EmailSignatureDesign
): boolean {
  const previousContactDetailsFieldsUseTheme = previousEmailSignatureDesign.contactDetailsIconUseThemeColor;
  const currentContactDetailsFieldsUseTheme = currentEmailSignatureDesign.contactDetailsIconUseThemeColor;

  if (previousContactDetailsFieldsUseTheme && currentContactDetailsFieldsUseTheme) {
    return !isEqual(
      previousEmailSignatureDesign.themeColor.toUpperCase(),
      currentEmailSignatureDesign.themeColor.toUpperCase()
    );
  } else if (!previousContactDetailsFieldsUseTheme && currentContactDetailsFieldsUseTheme) {
    return !isEqual(
      previousEmailSignatureDesign.contactDetailsIconColor?.toUpperCase(),
      currentEmailSignatureDesign.themeColor.toUpperCase()
    );
  } else if (previousContactDetailsFieldsUseTheme && !currentContactDetailsFieldsUseTheme) {
    return !isEqual(
      previousEmailSignatureDesign.themeColor.toUpperCase(),
      currentEmailSignatureDesign.contactDetailsIconColor?.toUpperCase()
    );
  } else
    return !isEqual(
      previousEmailSignatureDesign.contactDetailsIconColor?.toUpperCase(),
      currentEmailSignatureDesign.contactDetailsIconColor?.toUpperCase()
    );
}

/**
 * If the users give us an website link and do not give us the title, we will pick a default
 * title and save it in database. This function fills in all the blank title with default titles
 * @param localEmailSignatureObjectToUpdate The local email signature object that is going to be saved in the database
 */
function updateBlankTitlesForWebsiteLinksInContactDetails(
  localEmailSignatureObjectToUpdate: EmailSignatureV2
): void {
  forEach(localEmailSignatureObjectToUpdate.contactDetails, (contactDetailsField) => {
    const contactDetailsFieldId = contactDetailsField.id;
    const contactDetailsFieldType = contactDetailsField.type;
    const contactDetailsFieldURl = contactDetailsField.value;

    if (contactDetailsFieldType !== 'website') return;

    if (localEmailSignatureObjectToUpdate.contactDetails[contactDetailsFieldId].title) return;

    localEmailSignatureObjectToUpdate.contactDetails[contactDetailsFieldId].title =
      getDefaultTitleForWebsiteIfNoUserInput(contactDetailsFieldURl);
  });
}

/**
 * If the team admin changes the recipient email of a team email signature, we will update a new activation code and reset
 * userId, activatedAt and sendActivationEmailAt fields
 *
 * @param localEmailSignatureObjectToUpdate The local email signature object that is going to be saved in the database
 * @param initialEmailSignature The initial email signature object
 * @param currentUser The current user object
 */
function updateActivationCodeIfNecessary(
  localEmailSignatureObjectToUpdate: EmailSignatureV2,
  initialEmailSignature: EmailSignatureV2 | undefined,
  currentUser: User
): void {
  if (localEmailSignatureObjectToUpdate.userId !== currentUser.userId) {
    if (localEmailSignatureObjectToUpdate.recipientEmail !== initialEmailSignature?.recipientEmail) {
      delete localEmailSignatureObjectToUpdate.activatedAt;
      delete localEmailSignatureObjectToUpdate.sendActivationEmailAt;
      localEmailSignatureObjectToUpdate.userId = '';
      localEmailSignatureObjectToUpdate.activationCode = generateEntityId(32);
    }
  }
}

/**
 * Get the amount of space needed for a field in order to best display the text provided
 * @param displayText The text to be displayed in the field
 * @param font The font used to render the display text
 * @param displayIcon Whether or not icons are being displayed
 */
export function getContactDetailFieldWidth(
  displayText: string,
  font: string,
  displayIcon: boolean
): EmailSignatureContactDetailsFieldWidth {
  if (!displayText) return EmailSignatureContactDetailsFieldWidth.HALF_WIDTH;

  const fieldWidthLimitExceeded = contactDetailsFieldExceedsLimit(displayText, font);

  if (!displayIcon) {
    return EmailSignatureContactDetailsFieldWidth.FULL_WIDTH;
  } else if (fieldWidthLimitExceeded && displayIcon) {
    return EmailSignatureContactDetailsFieldWidth.FULL_WIDTH;
  } else if (!fieldWidthLimitExceeded && displayIcon) {
    return EmailSignatureContactDetailsFieldWidth.HALF_WIDTH;
  } else {
    return EmailSignatureContactDetailsFieldWidth.HALF_WIDTH;
  }
}

/**
 * Formats the type of a social link to proper spelling.
 * @param socialLinkType Type of the social link
 */
function formatEmailSignatureSocialLinksType(socialLinkType: EmailSignatureSocialLinkTypes): string {
  return formatUrlType(socialLinkType);
}

/**
 * When user hovers a social link in the preview section, we will display an outline. This
 * outline will have border radius that depends on the shape of the social icon
 * @param emailSignatureDesign The design object of the email signature
 */
function getBorderRadiusForSocialLinkOutline(emailSignatureDesign: EmailSignatureDesign): string | number {
  if (emailSignatureDesign.socialLinksShape === EmailSignatureShapes.CIRCLE) {
    return '50%';
  } else if (emailSignatureDesign.socialLinksShape === EmailSignatureShapes.SQUARE) {
    return 0;
  } else return '10px';
}

/**
 * The user's value input for contact details field will be displayed as is, except a website link.
 * We need to display the title for a website link
 * @param emailSignatureContactDetailsField The contact field we want to derive the value to display for
 */
function getActualContactDetailValueToDisplay(
  emailSignatureContactDetailsField: EmailSignatureContactDetailsField
): string {
  if (emailSignatureContactDetailsField.type === 'website') {
    if (emailSignatureContactDetailsField.title) return emailSignatureContactDetailsField.title;
    else return getDefaultTitleForWebsiteIfNoUserInput(emailSignatureContactDetailsField.value);
  } else {
    return emailSignatureContactDetailsField.value;
  }
}

/**
 * User's value input will be enhanced with extra prefix and formatted link
 * @param emailSignatureContactDetailsField The contact field we want to derive the link for
 * @param isInEditingMode Is this value for editing mode
 */
function getActualLinkForContactDetailsField(
  emailSignatureContactDetailsField: EmailSignatureContactDetailsField | undefined
): string {
  if (!emailSignatureContactDetailsField) return '';

  const actualLink = addPrefixToContactFieldValue(
    emailSignatureContactDetailsField.value,
    emailSignatureContactDetailsField.type
  );

  if (actualLink.startsWith('http')) {
    // If the link is a website link, we will use the proxy URL to that destination
    return buildUrl(NEXT_PUBLIC_BLINQ_FORWARD_BASE_URL, emailSignatureContactDetailsField.id);
  }

  return actualLink;
}

/**
 * If the user does not have a title for their personal email signature, we will pick one for them
 * @param emailSignatureNumber The number of the email signature
 */
function formatEmailSignatureDefaultTitle(emailSignatureNumber: number): string {
  return `Email signature #${emailSignatureNumber}`;
}

function getStandardCompactEmailSignature(
  standardCompactContactFields: EmailSignatureContactDetailsField[],
  standardCompactSocialLinks: EmailSignatureSocialLink[],
  currentEmailSignature: EmailSignatureV2
): EmailSignatureV2 {
  const contactFieldsOrder = map(standardCompactContactFields, (dummyContactField) => dummyContactField.id);
  const socialLinksOrder = map(standardCompactSocialLinks, (dummySocialLink) => dummySocialLink.id);

  return {
    ...currentEmailSignature,
    personalDetails: {
      firstName: 'John',
      lastName: 'Doe',
      jobTitle: 'Software Engineer',
      company: 'Blinq Technologies',
      headline: 'Blinq is trusted by thousands of users to share their professional identity every day.',
    },
    contactDetails: keyBy(standardCompactContactFields, 'id'),
    contactDetailsOrder: contactFieldsOrder,
    socialLinks: keyBy(standardCompactSocialLinks, 'id'),
    socialLinksOrder: socialLinksOrder,
    saveContact: {
      cardId: 'dummy',
      buttonColorUseThemeColor: currentEmailSignature.saveContact.buttonColorUseThemeColor,
      buttonColor: currentEmailSignature.saveContact.buttonColor,
      displaySaveContact: currentEmailSignature.saveContact.displaySaveContact,
    },
    images: [
      {
        id: 'im_dummy',
        externalLink: '',
        imageUrl: '',
        shape: EmailSignatureShapes.SQUIRCLE,
        size: EmailSignatureSizes.SMALL,
        type: 'profile-picture',
        isPrimary: true,
        originalImageUrl: '',
        crop: {
          aspect: undefined,
          height: 272,
          unit: 'px',
          width: 362,
          x: 0,
          y: 0,
        },
      },
      {
        id: 'im_dummy',
        externalLink: '',
        imageUrl: '',
        shape: EmailSignatureShapes.SQUIRCLE,
        size: EmailSignatureSizes.SMALL,
        type: 'profile-picture',
        isPrimary: false,
        originalImageUrl: '',
        crop: {
          aspect: undefined,
          height: 272,
          unit: 'px',
          width: 362,
          x: 0,
          y: 0,
        },
      },
    ],
  };
}

/**
 * Get the image title based on the type of the image
 * @param imageType The number of the email signature
 */
function getImageTitle(imageType: EmailSignatureImagesTypes): string {
  switch (imageType) {
    case 'company-logo':
      return 'Company logo';
    case 'profile-picture':
      return 'Profile picture';
    case 'other':
      return 'Other image';
    default:
      return 'Image';
  }
}

/**
 * When the user wants to make an image primary or secondary, the position of the image
 * will change, we need to display the message to explain the change
 * @param neighbourImage The neighbour image either a secondary or a primary image
 * @param currentImage The current image to be made a primary or secondary
 */
function getMakePrimaryOrSecondaryImageMessageForCompactLayout(
  neighbourImage: EmailSignatureImage,
  currentImage: EmailSignatureImage
): string {
  const position = currentImage.isPrimary ? 'below' : 'above';
  const referenceToTheNeighborImage =
    currentImage.type === neighbourImage.type
      ? `the other ${getImageTitle(neighbourImage.type)}`
      : `the ${getImageTitle(neighbourImage.type)}`;
  return `This will move this image ${position} ${lowerCase(referenceToTheNeighborImage)}`;
}

/**
 * When the admin is requesting a bulk create and they dont have any signatures, we will create templates based on their cards
 * We will filter out any "empty" signatures before returning
 * @param cards The cards the admin have access to
 */
function getTemplatesFromCards(cards: Card[]): EmailSignatureV2[] {
  // sort the cards according to its creation time stamp
  const sortedCards = orderBy(cards, (card) => card.creationTimestampMs);

  const emailSignatureTemplates = sortedCards.map((card) => {
    const baseEmailSignature = { ...initialEmailSignatureContextValues };
    const personalDetails = extractEmailSignaturePersonalDetailsFromCard(card);
    const [contactDetailsFields, contactDetailsFieldOrder] =
      extractEmailSignatureContactDetailsFieldsFromCard(card, initialEmailSignatureDesignConfig);
    const [socialLinks, socialLinksOrder] = extractEmailSignatureSocialLinksFromCard(card);

    const contactDetailsFieldsWithIconImages = mapValues(contactDetailsFields, (contactDetailsField) => ({
      ...contactDetailsField,
      iconUrl: generateContactDetailsIconImage(
        contactDetailsField.baseIcon,
        card.color || initialEmailSignatureDesignConfig.themeColor
      ),
    }));

    const socialLinksWithIconImages = mapValues(socialLinks, (socialLink) => ({
      ...socialLink,
      iconUrl: generateSocialLinkIconImage(socialLink.type, initialEmailSignatureDesignConfig),
    }));

    const signatureBasedOnCard = {
      ...baseEmailSignature,
      design: {
        ...baseEmailSignature.design,
        themeColor: card.color ?? '#f45a57',
      },
      socialLinks: socialLinksWithIconImages,
      socialLinksOrder,
      personalDetails,
      contactDetails: contactDetailsFieldsWithIconImages,
      contactDetailsOrder: contactDetailsFieldOrder,
    };

    return signatureBasedOnCard;
  });

  // Only take signatures with some details
  const emailSignatureTemplatesWithActualDetails = filter(
    emailSignatureTemplates,
    (emailSignatureTemplate) =>
      (!isEmpty(emailSignatureTemplate.contactDetailsOrder) ||
        !isEmpty(emailSignatureTemplate.socialLinksOrder)) &&
      !isObjectActuallyEmpty(emailSignatureTemplate.personalDetails)
  );
  // Only take 3 latest
  const templatesToUse = takeRight(emailSignatureTemplatesWithActualDetails, 3);
  return templatesToUse as EmailSignatureV2[];
}

/**
 * When the admin is requesting a bulk create and they dont have any signatures or cards, we give them this default template filled with dummy values
 */
function generateDefaultCompactEmailSignatureTemplate(): EmailSignatureV2 {
  const standardContactDetails: EmailSignatureContactDetailsField[] = [
    {
      baseIcon: 'envelope',
      iconUrl:
        generateContactDetailsIconImage('envelope', initialEmailSignatureDesignConfig.themeColor) || '',
      value: 'johndoe@gmail.com',
      label: '',
      id: 'ct_1',
      type: EmailSignatureContactDetailsFieldTypes.EMAIL,
      fieldWidth: EmailSignatureContactDetailsFieldWidth.HALF_WIDTH,
    },
    {
      baseIcon: 'phone-alt',
      iconUrl:
        generateContactDetailsIconImage('phone-alt', initialEmailSignatureDesignConfig.themeColor) || '',
      value: '0491 570 006',
      label: '',
      id: 'ct_2',
      type: EmailSignatureContactDetailsFieldTypes.PHONE,
      fieldWidth: EmailSignatureContactDetailsFieldWidth.HALF_WIDTH,
    },
    {
      baseIcon: 'map-marker-alt',
      iconUrl:
        generateContactDetailsIconImage('map-marker-alt', initialEmailSignatureDesignConfig.themeColor) || '',
      value: '42 Wallaby Way, Sydney',
      label: '',
      id: 'ct_3',
      type: EmailSignatureContactDetailsFieldTypes.ADDRESS,
      fieldWidth: EmailSignatureContactDetailsFieldWidth.HALF_WIDTH,
    },
    {
      baseIcon: 'browser',
      iconUrl: generateContactDetailsIconImage('browser', initialEmailSignatureDesignConfig.themeColor) || '',
      value: 'blinq.me',
      label: '',
      id: 'ct_4',
      type: EmailSignatureContactDetailsFieldTypes.WEBSITE,
      fieldWidth: EmailSignatureContactDetailsFieldWidth.HALF_WIDTH,
    },
  ];
  const standardSocialLinks: EmailSignatureSocialLink[] = [
    {
      type: EmailSignatureSocialLinkTypes.LINKEDIN,
      iconUrl:
        generateSocialLinkIconImage(
          EmailSignatureSocialLinkTypes.LINKEDIN,
          initialEmailSignatureDesignConfig
        ) || '',
      id: 'sc_1',
      value: 'fakeValue',
    },
    {
      type: EmailSignatureSocialLinkTypes.FACEBOOK,
      iconUrl:
        generateSocialLinkIconImage(
          EmailSignatureSocialLinkTypes.FACEBOOK,
          initialEmailSignatureDesignConfig
        ) || '',
      id: 'sc_2',
      value: 'fakeValue',
    },
    {
      type: EmailSignatureSocialLinkTypes.TWITTER,
      iconUrl:
        generateSocialLinkIconImage(
          EmailSignatureSocialLinkTypes.TWITTER,
          initialEmailSignatureDesignConfig
        ) || '',
      id: 'sc_3',
      value: 'fakeValue',
    },
    {
      type: EmailSignatureSocialLinkTypes.YOUTUBE,
      iconUrl:
        generateSocialLinkIconImage(
          EmailSignatureSocialLinkTypes.YOUTUBE,
          initialEmailSignatureDesignConfig
        ) || '',
      id: 'sc_4',
      value: 'fakeValue',
    },
  ];
  const baseEmailSignature = { ...initialEmailSignatureContextValues };
  const contactFieldsOrder = map(standardContactDetails, (dummyContactField) => dummyContactField.id);
  const socialLinksOrder = map(standardSocialLinks, (dummySocialLink) => dummySocialLink.id);

  return {
    ...baseEmailSignature,
    personalDetails: {
      firstName: 'John',
      lastName: 'Doe',
      jobTitle: 'Software Engineer',
      company: 'Blinq Technologies',
      headline: 'Blinq is trusted by thousands of users to share their professional identity every day.',
    },
    contactDetails: keyBy(standardContactDetails, 'id'),
    contactDetailsOrder: contactFieldsOrder,
    socialLinks: keyBy(standardSocialLinks, 'id'),
    socialLinksOrder: socialLinksOrder,
  };
}

/**
 * Get the firebase storage image link for the signature's main section
 * @param signatureId The signature id
 */
export const getSignatureFirebaseStorageImageLink = (signatureId: string): string =>
  `https://firebasestorage.googleapis.com/v0/b/${NEXT_PUBLIC_FIREBASE_PROJECT_ID}.appspot.com/o/email_signatures%2F${signatureId}?alt=media`;

/**
 * Get the proxy link of an email signature's image, the c query is just a dummy query to force the apple mail to fetch the image instead of using
 * the potentially outdated image in their cache storage. Which means retroactive update is impossible for apple mail.
 * @param imageId The image id
 */
export const getImageProxyLink = (imageId: string): string => {
  const imageLink = new URL(
    path.join(NEXT_PUBLIC_EMAIL_SIGNATURE_URL, `/${imageId}?c=${generateEntityId()}`)
  );

  return imageLink.toString();
};

/**
 * Get the firebase storage image link for the test signature's main section
 * @param signatureId The test signature id
 */
export const getTestSignatureFirebaseStorageImageLink = (signatureId: string): string =>
  `https://firebasestorage.googleapis.com/v0/b/${NEXT_PUBLIC_FIREBASE_PROJECT_ID}.appspot.com/o/email_signatures%2Ftemp%2F${signatureId}?alt=media`;

/**
 * Create proxy image links for signature's firebase storage links.
 * Uses GraphQL emailSignatureProxyImage mutation
 */
export async function createProxyLinksForImages(
  emailSignatureId: string,
  imagesToCreateProxyLinks: EmailSignatureProxyImage[]
): Promise<Record<string, string>> {
  const mutation = gql`
    mutation createProxyLinksForImages($email_signature_id: ID!, $images: [EmailSignatureProxyImageInput!]) {
      emailSignatureProxyImage(email_signature_id: $email_signature_id, images: $images) {
        id
        proxy_images {
          image_id
          url
        }
      }
    }
  `;
  const mutateResult = await apolloClient.mutate({
    mutation,
    variables: {
      email_signature_id: emailSignatureId,
      images: imagesToCreateProxyLinks.map(({ imageId, url }) => ({ image_id: imageId, url })),
    },
  });
  if (!mutateResult.errors) {
    const proxyImageArray = mutateResult.data.emailSignatureProxyImage
      .proxy_images as EmailSignatureProxyImage[];

    const object = proxyImageArray.reduce(
      (obj, item) => Object.assign(obj, { [item.imageId]: item.url }),
      {}
    );
    return object;
  } else {
    logger.error('emailSignatureProxyImage mutation failed', { error: mutateResult.errors });
    return {};
  }
}

/**
 * Extract and create proxy image links for signature's firebase storage links
 * @param emailSignature The email signature object
 */
async function createImageProxyLinksForImageBasedSignature(
  emailSignature: EmailSignatureV2,
  originalImageLinkGenerator?: (signatureId: string) => string
): Promise<Record<string, string>> {
  const imagesToCreateProxyLinks: EmailSignatureProxyImage[] = [];

  imagesToCreateProxyLinks.push({
    imageId: emailSignature.id,
    url: originalImageLinkGenerator
      ? originalImageLinkGenerator(emailSignature.id)
      : getSignatureFirebaseStorageImageLink(emailSignature.id),
  });

  if (emailSignature.banner?.imageUrl) {
    imagesToCreateProxyLinks.push({
      imageId: emailSignature.banner.id,
      url: emailSignature.banner.imageUrl,
    });
  }

  if (emailSignature.environmentalFooter) {
    imagesToCreateProxyLinks.push({
      imageId: emailSignature.environmentalFooter.id,
      url: emailSignature.environmentalFooter.iconUrl,
    });
  }

  return await createProxyLinksForImages(emailSignature.id, imagesToCreateProxyLinks);
}

/** When asking Vangogh to generate an image with a provieded font, the font must be in the correct format
 * This format is different to the format required by the css property: font-family.
 * This function converts from the css format to the format required by Vangogh
 */
const convertFontFamilyToFilenameFormat = (fontFamily: string): string => {
  switch (fontFamily) {
    case 'Verdana':
      return 'verdana';
    case 'Tahoma':
      return 'tahoma';
    case 'Trebuchet MS':
      return 'trebuchet';
    case 'Helvetica':
      return 'helvetica';
    case 'Times New Roman':
      return 'timesnewroman';
    case 'Garamond':
      return 'garamond';
    case 'Georgia':
      return 'georgia';
    case 'Courier New':
      return 'couriernew';
    case 'Arial':
    default:
      return 'arial';
  }
};

export {
  cropEmailSignatureImageToGivenShape,
  copyEmailSignatureToClipboard,
  convertContactIconSVGToImage,
  convertSocialIconSVGToImage,
  updateBlankTitlesForWebsiteLinksInContactDetails,
  updateActivationCodeIfNecessary,
  convertEnvironmentalFooterIconSVGToImage,
  formatEmailSignatureSocialLinksType,
  getBorderRadiusForSocialLinkOutline,
  getActualContactDetailValueToDisplay,
  getActualLinkForContactDetailsField,
  formatEmailSignatureDefaultTitle,
  getStandardCompactEmailSignature,
  getImageTitle,
  getMakePrimaryOrSecondaryImageMessageForCompactLayout,
  getTemplatesFromCards,
  generateDefaultCompactEmailSignatureTemplate,
  createImageProxyLinksForImageBasedSignature,
  convertFontFamilyToFilenameFormat,
};
